import type Proposal from "work/proposals/proposal";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type { ProposalFieldNameValue } from "work/proposals/proposal-fields/proposal-field";
import type { RedlineActionKey, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import type {
  ProposalContentOperation,
  ProposalNegotiationParty,
  ProposalResolutionDecisionOperation,
  ProposalResolutionDisposition,
  ProposalResolutionState,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  proposalCollectionItemHasIdentity,
  proposalCollectionItemReplaces,
} from "work/proposals/negotiation/proposal-operation-identity";
import {
  canonicalProposalCollectionItem,
  proposalSemanticJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";
import { proposalContentOperationTargetsResolutionTarget } from "work/proposals/negotiation/proposal-operation-containment";
import { proposalTimelinePathForFieldName } from "work/proposals/negotiation/proposal-field-timeline-path";
import { isProposalResolutionTargetResolvable } from "work/proposals/negotiation/proposal-resolution-target";
import { decodeProposalPointerSegment } from "work/proposals/negotiation/proposal-operation-projector";

type IdFactory = () => string;

type BuildProposalResolutionDecisionOperationsParams = {
  proposal: Proposal;
  resolutionStates: readonly ProposalResolutionState[];
  viewingParty: ProposalNegotiationParty;
  currentSubmissionId: string;
  /** Local work that predates the current redline action and must survive a bulk reject. */
  existingLocalOperations?: readonly ProposalContentOperation[];
  /** Content produced by this editor snapshot, used to pair child edits with a whole-item target. */
  contentOperations?: readonly ProposalContentOperation[];
  createId?: IdFactory;
};

/**
 * Converts explicit actions in the current redline session into canonical,
 * auditable resolution decisions. The envelope owns the target identity and
 * incoming value; legacy `unresolvedFields` metadata is never read or emitted.
 */
export function buildProposalResolutionDecisionOperations({
  proposal,
  resolutionStates,
  viewingParty,
  currentSubmissionId,
  existingLocalOperations = [],
  contentOperations = [],
  createId = () => crypto.randomUUID(),
}: BuildProposalResolutionDecisionOperationsParams): ProposalResolutionDecisionOperation[] {
  const currentTargets = resolutionStates.filter(
    (state) =>
      state.submissionId === currentSubmissionId && isProposalResolutionTargetResolvable(state, viewingParty),
  );
  if (currentTargets.length === 0) return [];

  const decisionsByConflictKey = new Map<string, ProposalResolutionDisposition>();
  for (const change of proposal.redline?.sessionHistory ?? []) {
    for (const state of currentTargets) {
      if (redlineChangeTargetsResolutionState(change, state)) {
        const disposition = dispositionForRedlineChange(change, state, proposal);
        // Aggregate collection actions occur before their item actions in the
        // legacy session history. Last matching action therefore gives a
        // specific item action precedence over an earlier "apply to all".
        decisionsByConflictKey.set(state.conflictKey, disposition);
      }
    }
  }

  // A collection item's edit history is a presentation concern and is not
  // guaranteed to use the same action or field shape across widgets. The
  // operation tree is authoritative: editing a leaf beneath an unresolved
  // whole-item add/remove is an alternative to that whole target.
  for (const state of currentTargets) {
    const matchingContent = contentOperations.filter((operation) =>
      proposalContentOperationTargetsResolutionTarget(operation, state),
    );
    if (matchingContent.length === 0) continue;

    const editsChildOfWholeItem = matchingContent.some(
      (operation) => Boolean(state.itemId) && operation.conflictKey.startsWith(`${state.conflictKey}:`),
    );
    if (editsChildOfWholeItem) {
      decisionsByConflictKey.set(state.conflictKey, "ProposeAlternative");
      continue;
    }

    // The operation-tree fallback classifies collection whole-item targets only.
    // A scalar target is authoritative from the redline session history above:
    // touching one field re-flattens every still-pending local edit into
    // contentOperations as a "preserved alternative", so classifying a scalar
    // from a matching content operation would silently promote an untouched
    // field's pending edit to ProposeAlternative — hiding its Accept/Reject
    // controls before the user has resolved it. Leave untouched scalars
    // Unresolved; an explicit action produces a session-history entry above.
    if (!state.itemId) continue;

    // A whole-item content operation (accept-as-add or reject-as-remove) is
    // classified by its resulting semantic value. When no materialized revision
    // is available to reclassify it, keep the disposition already derived from
    // the redline action instead of assuming an alternative: rejecting an
    // incoming add removes the item, so its resulting value is the baseline and
    // the decision is KeepPrevious. Emitting ProposeAlternative there would
    // contradict the operation's own resulting value and the server rejects it.
    const semanticDisposition = dispositionForSemanticResult(proposal, state);
    if (semanticDisposition) decisionsByConflictKey.set(state.conflictKey, semanticDisposition);
  }

  // A replace is one change carrying two targets: an add for the replacement and a removal of the
  // document it replaced. The editor renders them as one row (see `foldReplacedItemStates` in
  // `proposal-resolution-redline-adapter`), and a row-level action names only the replacement's item
  // id — so without this the removal stays permanently Unresolved, counted as a blocker, with no row
  // left anywhere to clear it. Its disposition is read from the resulting revision like any other, so
  // the pair can still disagree if the user resolves them through different controls.
  for (const state of currentTargets) {
    if (!state.itemId || decisionsByConflictKey.has(state.conflictKey)) continue;
    const decidedReplacement = currentTargets.some(
      (candidate) =>
        candidate !== state &&
        candidate.path === state.path &&
        Boolean(candidate.itemId) &&
        decisionsByConflictKey.has(candidate.conflictKey) &&
        collectionItemReplacesTarget(proposal, candidate, state.itemId!),
    );
    if (!decidedReplacement) continue;
    const disposition = dispositionForSemanticResult(proposal, state);
    if (disposition) decisionsByConflictKey.set(state.conflictKey, disposition);
  }

  const operations: ProposalResolutionDecisionOperation[] = [];
  for (const state of currentTargets) {
    let disposition = decisionsByConflictKey.get(state.conflictKey);
    if (
      disposition === "KeepPrevious" &&
      existingLocalOperations.some(
        (operation) =>
          operation.kind !== "ResolutionDecision" &&
          proposalContentOperationTargetsResolutionTarget(operation, state) &&
          !operationRevertsTargetToBaseline(operation, state),
      )
    ) {
      // Reject All rejects the counterparty's incoming value; it must not erase
      // disjoint work that was already queued by this party before the incoming
      // submission arrived. That work remains an explicit alternative. A pending
      // operation that only reverts the target to its baseline (a whole-item
      // removal of an incoming add, or a scalar field set back to the baseline) is
      // the reject itself, not disjoint alternative work, so it never forces
      // ProposeAlternative.
      disposition = "ProposeAlternative";
    }
    if (!disposition) continue;
    // A decision whose disposition matches the observed one is normally skipped as
    // value-idempotent. But when this command also carries a content operation that
    // changes the same incoming target, the server requires the target's decision
    // in that command, so the decision must still be emitted even though the
    // disposition value is unchanged. Skip it only when an equal decision is
    // already queued for that target: the active editor re-flattens earlier edits
    // on every keystroke and must not re-emit decisions it already produced.
    const changesTarget = contentOperations.some(
      (operation) =>
        operation.kind !== "ResolutionDecision" &&
        proposalContentOperationTargetsResolutionTarget(operation, state),
    );
    const alreadyDecided = existingLocalOperations.some(
      (operation) =>
        operation.kind === "ResolutionDecision" && operation.conflictKey === state.conflictKey,
    );
    if (disposition === state.disposition && (!changesTarget || alreadyDecided)) continue;
    if (state.targetValueExists && !Object.prototype.hasOwnProperty.call(state, "targetValue")) {
      throw new Error(`Resolution target '${state.conflictKey}' is missing its canonical target value.`);
    }

    operations.push({
      operationId: createId(),
      kind: "ResolutionDecision",
      path: state.path,
      conflictKey: state.conflictKey,
      ...(state.itemId ? { itemId: state.itemId } : {}),
      expectedValue: state.disposition,
      value: disposition,
      ...(state.targetValueExists ? { targetValue: structuredClone(state.targetValue) } : {}),
    });
  }

  return operations;
}

/**
 * Whether `replacementTarget`'s item is the document that replaced `replacedItemId`.
 *
 * Read from the resulting revision first, because `replacesId` is document lineage the editor owns
 * rather than part of the decision protocol. A REJECTED replacement is no longer in that revision at
 * all — rejecting it puts the replaced document back — so the published target is the remaining
 * witness of the lineage. It carries `replacesId`: the canonical document value is an id plus that
 * one property, and nothing else.
 */
function collectionItemReplacesTarget(
  proposal: Proposal,
  replacementTarget: ProposalResolutionState,
  replacedItemId: string,
): boolean {
  const collection = readJSONPointer(proposal.redlinedRevision?.toJSON() ?? ({} as ProposalJSON), replacementTarget.path);
  if (collection.exists && Array.isArray(collection.value)) {
    const item = collection.value.find((value) =>
      proposalCollectionItemHasIdentity(value, replacementTarget.itemId!),
    );
    if (item !== undefined) return proposalCollectionItemReplaces(item, replacedItemId);
  }
  return (
    replacementTarget.targetValueExists &&
    proposalCollectionItemReplaces(replacementTarget.targetValue, replacedItemId)
  );
}

function operationRevertsTargetToBaseline(
  operation: ProposalContentOperation,
  state: ProposalResolutionState,
): boolean {
  // Only the target's own whole-item/scalar operation can revert it. A child-leaf
  // edit keeps a distinct conflict key and is a genuine alternative, so it is not
  // matched here.
  if (operation.kind === "ResolutionDecision") return false;
  if (operation.conflictKey.toLowerCase() !== state.conflictKey.toLowerCase()) return false;

  // Removing the whole item of an incoming add whose baseline is absent reverts the
  // target to its (absent) baseline.
  if (operation.kind === "CollectionItemRemoved" && Boolean(state.itemId)) {
    return !state.baselineValueExists;
  }

  // A scalar field set back to the baseline value reverts the target to its
  // baseline. Either way the operation is the reject decision (KeepPrevious), not
  // disjoint alternative work to preserve.
  if (operation.kind === "FieldSet") {
    return state.baselineValueExists
      ? proposalSemanticJsonValuesEqual(operation.value, state.baselineValue)
      : operation.value === undefined;
  }

  // A collection leaf written back to its baseline is the reject too: a deferred fee's `feeRate`
  // set back to null, say. Without this, the reject's own leaf update read as queued alternative
  // work and turned its KeepPrevious into ProposeAlternative (#6628).
  const leafSegments = resolutionCollectionLeafSegments(state);
  if (operation.kind === "CollectionItemUpdated" && leafSegments.length > 0) {
    return semanticValuesEqual(
      optionalCollectionLeaf(readNestedValue(operation.value, leafSegments)),
      canonicalStateValue(state, "baseline"),
    );
  }

  return false;
}

function dispositionForRedlineChange(
  change: RedlineChange,
  state: ProposalResolutionState,
  proposal: Proposal,
): ProposalResolutionDisposition {
  if (!change.isResolved) return "Unresolved";

  switch (change.action.toString() as RedlineActionKey) {
    case "Undo":
    case "UndoAdd":
    case "UndoEdit":
    case "UndoRemove":
    case "UndoAccept":
    case "UndoReject":
      return "Unresolved";
  }

  const result = resolutionResult(proposal, state);
  if (result) return dispositionForSemanticResult(proposal, state)!;

  // Isolated domain tests and imported legacy redlines may not expose a
  // materialized revision. Production canonical editors always take the
  // semantic branch above.
  const action = change.action.toString() as RedlineActionKey;
  return action === "Accept"
    ? "AcceptIncoming"
    : action === "Reject"
      ? "KeepPrevious"
      : "ProposeAlternative";
}

function dispositionForSemanticResult(
  proposal: Proposal,
  state: ProposalResolutionState,
): ProposalResolutionDisposition | undefined {
  const result = resolutionResult(proposal, state);
  if (!result) return undefined;
  if (semanticValuesEqual(result, canonicalStateValue(state, "target"), state.itemId ? state.path : undefined)) {
    return "AcceptIncoming";
  }
  if (semanticValuesEqual(result, canonicalStateValue(state, "baseline"), state.itemId ? state.path : undefined)) {
    return "KeepPrevious";
  }
  return "ProposeAlternative";
}

type SemanticValue = { exists: boolean; value?: unknown };

function canonicalStateValue(
  state: ProposalResolutionState,
  kind: "baseline" | "target",
): SemanticValue {
  const exists = kind === "baseline" ? state.baselineValueExists : state.targetValueExists;
  const value = kind === "baseline" ? state.baselineValue : state.targetValue;
  return exists ? { exists: true, value } : { exists: false };
}

function resolutionResult(proposal: Proposal, state: ProposalResolutionState): SemanticValue | undefined {
  const proposalJSON = proposal.redlinedRevision?.toJSON();
  if (!proposalJSON) return undefined;

  if (!state.itemId) return readJSONPointer(proposalJSON, state.path);

  const collection = readJSONPointer(proposalJSON, state.path);
  if (!collection.exists || !Array.isArray(collection.value)) return { exists: false };
  const item = collection.value.find((value) => proposalCollectionItemHasIdentity(value, state.itemId!));
  if (item === undefined) return { exists: false };

  const leafSegments = resolutionCollectionLeafSegments(state);
  if (leafSegments.length === 0) return { exists: true, value: item };
  return optionalCollectionLeaf(readNestedValue(item, leafSegments));
}

/**
 * The server stores an optional property of a collection item that is null as absent
 * (`ProposalCollectionValuePolicy.Sanitize`), and reads baselines and results that way. A deferred fee
 * kept as it was reads `feeRate: null` locally; read as present, it was a third value, and the decision
 * went out as ProposeAlternative against a result equal to the baseline (#6628).
 */
function optionalCollectionLeaf(leaf: SemanticValue): SemanticValue {
  return leaf.exists && leaf.value === null ? { exists: false } : leaf;
}

function resolutionCollectionLeafSegments(state: ProposalResolutionState): string[] {
  if (!state.itemId) return [];
  const prefix = `${state.path.slice(1).replaceAll("/", ":")}:${state.itemId}`;
  if (state.conflictKey === prefix || !state.conflictKey.startsWith(`${prefix}:`)) return [];
  return state.conflictKey.slice(prefix.length + 1).split(":").filter(Boolean);
}

function readJSONPointer(proposalJSON: ProposalJSON, pointer: string): SemanticValue {
  if (!pointer.startsWith("/")) return { exists: false };
  const segments = pointer.slice(1).split("/").map(decodeProposalPointerSegment);
  return readNestedValue(proposalJSON, segments);
}

function readNestedValue(value: unknown, segments: readonly string[]): SemanticValue {
  let current = value;
  for (const segment of segments) {
    if (!isRecord(current) || !Object.prototype.hasOwnProperty.call(current, segment)) {
      return { exists: false };
    }
    current = current[segment];
  }
  // Domain serializers retain optional properties with `undefined`. On the
  // command wire that is the same semantic state as a missing property, while
  // literal null remains an explicit existing value.
  return current === undefined ? { exists: false } : { exists: true, value: current };
}

function semanticValuesEqual(left: SemanticValue, right: SemanticValue, collectionPath?: string): boolean {
  const leftValue = collectionPath ? canonicalProposalCollectionItem(collectionPath, left.value) : left.value;
  const rightValue = collectionPath ? canonicalProposalCollectionItem(collectionPath, right.value) : right.value;
  return (
    left.exists === right.exists &&
    (!left.exists || jsonValuesEqual(leftValue, rightValue))
  );
}

function jsonValuesEqual(left: unknown, right: unknown): boolean {
  return proposalSemanticJsonValuesEqual(left, right);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function redlineChangeTargetsResolutionState(
  change: RedlineChange,
  state: ProposalResolutionState,
): boolean {
  const pathPrefix = resolutionPathPrefix(change.field.name);
  if (!pathPrefix || (state.path !== pathPrefix && !state.path.startsWith(`${pathPrefix}/`))) return false;

  const fieldItemId = change.field.id?.value;
  if (!fieldItemId) return true;
  return state.itemId === fieldItemId || state.itemId?.startsWith(`${fieldItemId}:`) === true;
}

function resolutionPathPrefix(fieldName: ProposalFieldNameValue): string | undefined {
  return proposalTimelinePathForFieldName(fieldName);
}
